import { Box, ButtonBase, Drawer, Typography, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { ChevronRightIcon } from "../../../icons";
import { CurrencyIcon } from "../../shared";
import { DEPOSIT_NETWORKS, DepositNetwork, fetchNetworks } from "./networks";

export type NetworkPickerMode = "deposit" | "withdraw";

interface NetworkPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Куда навигировать после выбора сети. */
  mode?: NetworkPickerMode;
  /**
   * Опциональный список сетей, который переопределяет глобальный.
   * Используется, когда пользователь уже выбрал конкретную валюту
   * и мы показываем только её сети.
   */
  networksOverride?: DepositNetwork[];
  /**
   * Заголовок и подзаголовок можно переопределить (например,
   * когда выбрана валюта — «Сети USDT»).
   */
  title?: string;
  subtitle?: string;
}

const MODE_CONFIG: Record<NetworkPickerMode, { title: string; subtitle: string; path: string }> = {
  deposit: {
    title: "Выберите сеть",
    subtitle: "Убедитесь, что выбранная сеть совпадает с сетью отправителя",
    path: "/deposit",
  },
  withdraw: {
    title: "Выберите сеть",
    subtitle: "Выберите сеть, в которой хотите получить средства",
    path: "/withdraw",
  },
};

export function NetworkPickerDrawer({
  open,
  onClose,
  mode = "deposit",
  networksOverride,
  title,
  subtitle,
}: NetworkPickerDrawerProps) {
  const navigate = useNavigate();
  const config = MODE_CONFIG[mode];

  const { data, isLoading } = useQuery({
    queryFn: fetchNetworks,
    queryKey: ["networks"],
    staleTime: 60_000,
    // Если пришёл пресет — не грузим сети с бэка вообще.
    enabled: !networksOverride,
  });
  const networks =
    networksOverride && networksOverride.length > 0
      ? networksOverride
      : data && data.length > 0
        ? data
        : DEPOSIT_NETWORKS;

  const handleSelect = (network: DepositNetwork) => {
    onClose();
    // Одна сеть может содержать несколько монет — обязательно передаём и
    // currency, иначе на странице депозита/вывода будет неоднозначность.
    const params = new URLSearchParams({
      network: network.id,
      currency: network.currency,
    });
    navigate(`${config.path}?${params.toString()}`);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // Поверх кастомной нижней навигации (у неё zIndex: 99999)
      sx={{ zIndex: 100010 }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" },
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 28px",
          backgroundImage: "none",
        },
      }}
    >
      {/* Хендл-бар */}
      <Box
        sx={(theme) => ({
          width: "44px",
          height: "4px",
          borderRadius: "4px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />

      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 600,
          textAlign: "center",
          marginBottom: "4px",
          color: "text.primary",
        }}
      >
        {title ?? config.title}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: "text.secondary",
          textAlign: "center",
          marginBottom: "18px",
        }}
      >
        {subtitle ?? config.subtitle}
      </Typography>

      {isLoading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
          <CircularProgress size={24} />
        </Box>
      ) : null}
      <Box sx={{ display: "grid", gap: "8px" }}>
        {networks.map((network) => (
          <ButtonBase
            key={network.id}
            onClick={() => handleSelect(network)}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: "14px",
              width: "100%",
              padding: "12px 14px",
              borderRadius: "18px",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.04)",
              transition: "background 0.15s",
              "&:active": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)",
              },
            })}
          >
            <CurrencyIcon
              value={network.logoUrl}
              size={42}
              fallbackLetter={network.standard || network.id}
              alt={network.name}
              sx={(theme) => ({
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
              })}
            />

            <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                {network.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  marginTop: "2px",
                }}
              >
                {network.currency} · {network.standard}
              </Typography>
            </Box>

            <Box
              sx={{
                color: "text.secondary",
                opacity: 0.6,
                display: "flex",
                flexShrink: 0,
              }}
            >
              <ChevronRightIcon />
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Drawer>
  );
}
